import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  ListRenderItemInfo,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';

import botAvatar from '/asset/chat/bot-avatar.png';
import Icon from '/component/base/Icon';
import LoadingIndicator from '/component/base/LoadingIndicator';
import {nobel} from '/theme/color/on.color';

import {styles} from '../ChatModal.style';
import {QuickSuggestion} from '../constants/ChatModal.constants';
import {
  CHAT_TEXT,
  getAskAboutSuggestionAccessibilityLabel,
  getSendSuggestionAccessibilityLabel,
} from '../constants/chatText.constants';
import {ChatMessageListItem} from '../types/ChatModal.types';

interface ChatMessagesPanelProps {
  messagesListRef: React.RefObject<FlatList<ChatMessageListItem>>;
  textInputRef: React.RefObject<TextInput>;
  messagesLength: number;
  isActiveConversationFetchingHistory: boolean;
  isActiveConversationLoading: boolean;
  primaryColor: string;
  dynamicStyles: any;
  activeQuickSuggestions: QuickSuggestion[];
  loadingStatusMessages: string[];
  suggestions: string[];
  inputResetKey: number;
  messageItems: ChatMessageListItem[];
  renderMessageItem: (
    info: ListRenderItemInfo<ChatMessageListItem>,
  ) => React.ReactElement | null;
  keyExtractor: (item: ChatMessageListItem) => string;
  onMessagesContentSizeChange: () => void;
  onSuggestionPress: (suggestion: string) => void;
  onInputFocus: () => void;
  onSendText: (text: string) => Promise<boolean>;
  ts: (key: string, fallback?: string) => string;
}

const ChatMessagesPanel: React.FC<ChatMessagesPanelProps> = ({
  messagesListRef,
  textInputRef,
  messagesLength,
  isActiveConversationFetchingHistory,
  isActiveConversationLoading,
  primaryColor,
  dynamicStyles,
  activeQuickSuggestions,
  loadingStatusMessages,
  suggestions,
  inputResetKey,
  messageItems,
  renderMessageItem,
  keyExtractor,
  onMessagesContentSizeChange,
  onSuggestionPress,
  onInputFocus,
  onSendText,
  ts,
}) => {
  const [draftText, setDraftText] = useState('');
  const skipNextTextChange = useRef(false);

  useEffect(() => {
    setDraftText('');
  }, [inputResetKey]);

  const clearLocalInput = useCallback(() => {
    setDraftText('');
    requestAnimationFrame(() => {
      textInputRef.current?.clear();
    });
  }, [textInputRef]);

  const handleSendPress = useCallback(async () => {
    if (isActiveConversationLoading) {
      return;
    }

    const sent = await onSendText(draftText);
    if (sent) {
      clearLocalInput();
    }
  }, [clearLocalInput, draftText, isActiveConversationLoading, onSendText]);

  const handleKeyPress = useCallback(
    (event: any) => {
      const key = event?.nativeEvent?.key;
      if (key === 'Enter') {
        skipNextTextChange.current = true;
        event.preventDefault?.();
        handleSendPress();
      }
    },
    [handleSendPress],
  );

  const handleChangeText = useCallback((text: string) => {
    if (skipNextTextChange.current) {
      skipNextTextChange.current = false;
      setDraftText('');
      return;
    }

    setDraftText(text);
  }, []);

  const handleSuggestionChipPress = useCallback(
    async (suggestion: string) => {
      clearLocalInput();
      await onSuggestionPress(suggestion);
    },
    [clearLocalInput, onSuggestionPress],
  );

  const visibleMessageItems = isActiveConversationFetchingHistory
    ? []
    : messageItems;

  return (
    <>
      <FlatList
        ref={messagesListRef}
        data={visibleMessageItems}
        renderItem={renderMessageItem}
        keyExtractor={keyExtractor}
        style={styles.messagesContainer}
        keyboardShouldPersistTaps="handled"
        initialNumToRender={12}
        maxToRenderPerBatch={12}
        windowSize={7}
        removeClippedSubviews
        onContentSizeChange={onMessagesContentSizeChange}
        contentContainerStyle={[
          styles.messagesContent,
          isActiveConversationFetchingHistory
            ? styles.messagesContentCentered
            : null,
          dynamicStyles.messagesContentInset,
        ]}
        showsVerticalScrollIndicator={false}
        ListHeaderComponent={
          <>
            {isActiveConversationFetchingHistory ? (
              <View style={styles.centeredLoadingContainer}>
                <LoadingIndicator
                  size="small"
                  style={styles.centeredLoadingIndicator}
                />
                <Text
                  style={[
                    styles.centeredLoadingText,
                    dynamicStyles.loadingText,
                  ]}>
                  {ts('chat.loadingHistory', CHAT_TEXT.loadingHistory)}
                </Text>
              </View>
            ) : messagesLength === 0 ? (
              <View style={styles.welcomeContainer}>
                <Image source={botAvatar} style={styles.welcomeAvatarImage} />
                <Text
                  style={[
                    styles.welcomeHeading,
                    dynamicStyles.welcomeHeadingText,
                  ]}>
                  {CHAT_TEXT.welcomeHeading}
                </Text>
                <Text
                  style={[
                    styles.welcomeSubtitle,
                    dynamicStyles.welcomeSubtitleText,
                  ]}>
                  {CHAT_TEXT.welcomeSubtitle}
                </Text>
                <View style={styles.welcomeSuggestionsGrid}>
                  {activeQuickSuggestions.map((qs, index) => (
                    <TouchableOpacity
                      key={index}
                      style={[
                        styles.welcomeSuggestionCard,
                        dynamicStyles.welcomeSuggestionCard,
                      ]}
                      onPress={() => onSuggestionPress(qs.query)}
                      disabled={isActiveConversationLoading}
                      accessibilityLabel={getAskAboutSuggestionAccessibilityLabel(
                        qs.label,
                      )}>
                      <Text
                        style={[
                          styles.welcomeSuggestionLabel,
                          dynamicStyles.welcomeSuggestionLabel,
                        ]}>
                        {qs.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            ) : null}
          </>
        }
        ListFooterComponent={
          <>
            {!isActiveConversationFetchingHistory &&
            messagesLength > 0 &&
            isActiveConversationLoading ? (
              <View style={styles.loadingContainer}>
                {(loadingStatusMessages.length > 0
                  ? loadingStatusMessages
                  : [ts('chat.typing', CHAT_TEXT.typingFallback)]
                ).map((message, index, allMessages) => {
                  const isLatestMessage = index === allMessages.length - 1;

                  return (
                    <View key={`${message}-${index}`} style={styles.loadingRow}>
                      <View style={styles.loadingIndicatorSlot}>
                        {isLatestMessage ? (
                          <ActivityIndicator
                            size="small"
                            color={primaryColor}
                          />
                        ) : null}
                      </View>
                      <Text
                        style={[styles.loadingText, dynamicStyles.loadingText]}>
                        {message}
                      </Text>
                    </View>
                  );
                })}
              </View>
            ) : null}
          </>
        }
      />

      {suggestions.length > 0 ? (
        <View style={styles.suggestionsContainer}>
          <View style={styles.suggestionsRow}>
            {suggestions.map((suggestion, index) => (
              <TouchableOpacity
                key={`${suggestion}-${index}`}
                style={[
                  styles.suggestionChip,
                  dynamicStyles.suggestionChip,
                  isActiveConversationLoading
                    ? dynamicStyles.suggestionChipDisabled
                    : dynamicStyles.suggestionChipEnabled,
                ]}
                disabled={isActiveConversationLoading}
                onPress={() => handleSuggestionChipPress(suggestion)}
                accessibilityLabel={getSendSuggestionAccessibilityLabel(
                  suggestion,
                )}>
                <Text
                  numberOfLines={2}
                  style={[
                    styles.suggestionChipText,
                    dynamicStyles.suggestionChipText,
                  ]}>
                  {suggestion}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      ) : null}

      <View style={[styles.inputContainer, dynamicStyles.inputContainerInset]}>
        <View style={styles.textInputContainer}>
          <TextInput
            ref={textInputRef}
            style={[styles.textInput, dynamicStyles.textInput]}
            value={draftText}
            onChangeText={handleChangeText}
            onFocus={onInputFocus}
            onKeyPress={handleKeyPress}
            onSubmitEditing={handleSendPress}
            returnKeyType="send"
            submitBehavior="blurAndSubmit"
            placeholder={ts('chat.placeholder', CHAT_TEXT.placeholder)}
            placeholderTextColor={nobel}
            multiline
            maxLength={1000}
            editable={!isActiveConversationLoading}
          />
          <TouchableOpacity
            style={[
              styles.sendButton,
              draftText.trim() && !isActiveConversationLoading
                ? dynamicStyles.sendButtonEnabled
                : dynamicStyles.sendButtonDisabled,
            ]}
            onPress={handleSendPress}
            disabled={!draftText.trim() || isActiveConversationLoading}
            accessibilityLabel={CHAT_TEXT.sendMessageAccessibilityLabel}>
            <Icon name="send" style={styles.sendIcon} />
          </TouchableOpacity>
        </View>
      </View>
      <View
        style={[
          styles.disclaimerContainer,
          dynamicStyles.disclaimerContainerInset,
        ]}>
        <Text style={[styles.disclaimerText, dynamicStyles.disclaimerText]}>
          {CHAT_TEXT.aiDisclaimer}
        </Text>
      </View>
    </>
  );
};

export default ChatMessagesPanel;
